import { useState } from 'react';
import {
  paisesConfig,
  top10PaisTypeConfig,
  top10PaisReglas,
  top10PaisGlobalStats,
  top10PaisMap,
  getTop10ByPaisYTipo,
  getEstadosForPais,
  getCiudadesForEstado,
  formatearNumeroPais,
  type PaisId,
  type Top10EntityTypePais,
  type Top10EntryPais,
} from '@/mocks/top10Pais';

const movementColors: Record<string, { icon: string; color: string; bg: string }> = {
  up: { icon: 'ri-arrow-up-line', color: 'text-emerald-500', bg: 'bg-emerald-50' },
  down: { icon: 'ri-arrow-down-line', color: 'text-red-500', bg: 'bg-red-50' },
  stable: { icon: 'ri-subtract-line', color: 'text-foreground-400', bg: 'bg-foreground-100' },
};

const trendDots: Record<string, string> = {
  exploding: 'bg-rose-400',
  trending: 'bg-amber-400',
  growing: 'bg-emerald-400',
  active: 'bg-sky-400',
  stable: 'bg-foreground-300',
  declining: 'bg-red-400',
};

function Top10PaisCard({ entry, typeColor }: { entry: Top10EntryPais; typeColor: string }) {
  const movement = movementColors[entry.movement];

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-background-300/60 transition-all cursor-pointer group">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center">
        <span className={`text-xs font-bold ${entry.position <= 3 ? typeColor : 'text-foreground-600'}`}>#{entry.position}</span>
      </div>

      <div className={`flex-shrink-0 w-5 h-5 rounded-md ${movement.bg} flex items-center justify-center`}>
        <i className={`${movement.icon} ${movement.color} text-[10px]`}></i>
      </div>

      <img src={entry.logoUrl} alt={entry.entityName} className="w-9 h-9 rounded-lg object-cover flex-shrink-0" loading="lazy" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-xs font-bold text-foreground-900 truncate">{entry.entityName}</p>
          {entry.isLive && (
            <span className="flex-shrink-0 flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-red-50 text-red-600 text-[8px] font-bold">
              <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse"></span>
              EN VIVO
            </span>
          )}
        </div>
        <p className="text-[10px] text-foreground-500 truncate">{entry.ciudad}</p>
      </div>

      <div className="flex-shrink-0 text-right">
        <p className="text-xs font-bold text-foreground-900">{entry.scoreLabel}</p>
        <span className={`text-[10px] font-semibold ${entry.scoreChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
          {entry.scoreChange >= 0 ? '+' : ''}{entry.scoreChange}
        </span>
      </div>
    </div>
  );
}

export default function Top10PaisSection() {
  const [activePais, setActivePais] = useState<PaisId>('mx');
  const [activeType, setActiveType] = useState<Top10EntityTypePais>('radios');
  const [activeEstado, setActiveEstado] = useState<string>('');
  const [showReglas, setShowReglas] = useState(false);

  const paisInfo = paisesConfig.find(p => p.id === activePais);
  const typeInfo = top10PaisTypeConfig[activeType];
  const estados = getEstadosForPais(activePais);
  const ciudades = activeEstado ? getCiudadesForEstado(activePais, activeEstado) : [];

  const fullEntries = getTop10ByPaisYTipo(activePais, activeType);
  const currentEntries = activeEstado
    ? fullEntries.filter(e => e.estado === activeEstado)
    : fullEntries;

  const availableTypes = (Object.keys(top10PaisMap[activePais]) as Top10EntityTypePais[]).filter(
    k => top10PaisMap[activePais][k] && top10PaisMap[activePais][k].length > 0
  );
  const totalForPais = fullEntries.length;

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        {/* header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 mb-4">
            <i className="ri-earth-line text-emerald-600 text-sm"></i>
            <span className="text-xs font-bold text-emerald-700 whitespace-nowrap">COMPETENCIA REGIONAL</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Top 10 por País
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            Rankings locales en 5 países. Filtros de país, estado y ciudad. Misma actualización automática cada 60 segundos que el Top 10 Global.
          </p>

          {/* mini stats */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-6">
            <div className="text-center">
              <p className="text-lg font-bold text-foreground-900">{top10PaisGlobalStats.totalPaises}</p>
              <p className="text-xs text-foreground-500">Países</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-foreground-900">{top10PaisGlobalStats.totalEstados}</p>
              <p className="text-xs text-foreground-500">Estados</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-foreground-900">{top10PaisGlobalStats.totalCiudades}</p>
              <p className="text-xs text-foreground-500">Ciudades</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-foreground-900">{top10PaisGlobalStats.totalEntidadesRankeadas}</p>
              <p className="text-xs text-foreground-500">Entidades</p>
            </div>
          </div>
        </div>

        {/* country tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {paisesConfig.map((pais) => (
            <button
              key={pais.id}
              onClick={() => { setActivePais(pais.id); setActiveEstado(''); setShowReglas(false); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                activePais === pais.id && !showReglas
                  ? `${pais.bg} ${pais.color}`
                  : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
              }`}
            >
              <span className="text-base">{pais.flag}</span>
              {pais.nombre}
            </button>
          ))}
          <button
            onClick={() => setShowReglas(!showReglas)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
              showReglas ? 'bg-violet-50 text-violet-600' : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
            }`}
          >
            <i className="ri-scales-line text-sm"></i>
            Reglas
          </button>
        </div>

        {/* content */}
        {!showReglas ? (
          <>
            {/* pais description */}
            {paisInfo && (
              <div className="mb-4 p-3 rounded-xl bg-background-100 border border-background-200/70 flex items-start gap-3">
                <span className="text-xl">{paisInfo.flag}</span>
                <div>
                  <p className="text-sm text-foreground-600">{paisInfo.description}</p>
                  <p className="text-xs text-foreground-400 mt-1">{paisInfo.totalRadios} radios · {paisInfo.totalEntidades} entidades totales</p>
                </div>
              </div>
            )}

            {/* type tabs */}
            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              {availableTypes.map((type) => {
                const info = top10PaisTypeConfig[type];
                const count = getTop10ByPaisYTipo(activePais, type).length;
                return (
                  <button
                    key={type}
                    onClick={() => setActiveType(type)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                      activeType === type ? `${info.bg} ${info.color}` : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    <i className={`${info.icon} text-xs`}></i>
                    {info.label}
                    <span className="text-[10px] opacity-60">({count})</span>
                  </button>
                );
              })}
            </div>

            {/* state filter */}
            {estados.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mb-4">
                <span className="text-xs text-foreground-400 mr-1 whitespace-nowrap">Estado:</span>
                <button
                  onClick={() => setActiveEstado('')}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    !activeEstado ? 'bg-foreground-800 text-background-50' : 'text-foreground-500 hover:text-foreground-700 bg-background-100'
                  }`}
                >
                  Todos
                </button>
                {estados.map((est) => (
                  <button
                    key={est}
                    onClick={() => setActiveEstado(est)}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                      activeEstado === est ? 'bg-foreground-800 text-background-50' : 'text-foreground-500 hover:text-foreground-700 bg-background-100'
                    }`}
                  >
                    {est}
                  </button>
                ))}
              </div>
            )}

            {/* podium top 3 */}
            {currentEntries.length >= 3 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {currentEntries.slice(0, 3).map((entry, idx) => {
                  const movement = movementColors[entry.movement];
                  const podiums = [
                    { label: 'ORO', color: 'from-amber-400 to-yellow-500', textColor: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
                    { label: 'PLATA', color: 'from-slate-400 to-slate-500', textColor: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' },
                    { label: 'BRONCE', color: 'from-orange-400 to-orange-600', textColor: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
                  ];
                  const podium = podiums[idx];

                  return (
                    <div key={entry.entityName} className={`relative p-4 rounded-2xl border ${podium.border} ${podium.bg} transition-all hover:shadow-sm cursor-pointer`}>
                      <div className={`absolute top-3 right-3 px-2 py-0.5 rounded-md bg-gradient-to-r ${podium.color} text-white text-[10px] font-bold`}>
                        {podium.label}
                      </div>

                      <div className="flex items-center gap-3 mb-3">
                        <img src={entry.logoUrl} alt="" className="w-12 h-12 rounded-xl object-cover" loading="lazy" />
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-foreground-900 truncate">{entry.entityName}</p>
                          <p className="text-xs text-foreground-500 truncate">{entry.ciudad}, {entry.estado}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-lg font-black text-foreground-800">#{entry.position}</span>
                        <div className={`w-5 h-5 rounded-md ${movement.bg} flex items-center justify-center`}>
                          <i className={`${movement.icon} ${movement.color} text-[10px]`}></i>
                        </div>
                        <span className={`text-sm font-bold ${typeInfo.color}`}>{entry.scoreLabel}</span>
                        <span className={`text-xs font-semibold ${entry.scoreChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {entry.scoreChange >= 0 ? '+' : ''}{entry.scoreChange}
                        </span>
                        {entry.isLive && (
                          <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-bold">
                            <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse"></span>
                            EN VIVO
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* list 4-10 */}
            <div className="space-y-1.5">
              {currentEntries.slice(3, 10).map((entry) => (
                <Top10PaisCard key={entry.entityName} entry={entry} typeColor={typeInfo.color} />
              ))}
            </div>

            {currentEntries.length === 0 && (
              <div className="text-center py-12">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-background-100 flex items-center justify-center mb-3">
                  <i className="ri-map-pin-line text-foreground-300 text-xl"></i>
                </div>
                <p className="text-sm text-foreground-500">No hay entidades rankeadas en esta combinación</p>
              </div>
            )}

            {/* CTA */}
            <div className="mt-8 text-center flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="/top-10-pais"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-bold hover:from-emerald-600 hover:to-teal-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-earth-line"></i>
                Ver Top 10 por País Completo
              </a>
              <a
                href="/top-10-global"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-background-100 border border-background-200/70 text-foreground-700 text-sm font-bold hover:bg-background-200/50 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-global-line"></i>
                Ver Top 10 Global
              </a>
            </div>
          </>
        ) : (
          /* reglas view */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {top10PaisReglas.map((regla) => (
              <div key={regla.id} className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${regla.gradiente} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${regla.icono} text-white text-lg`}></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground-900 mb-2">{regla.titulo}</h4>
                    <p className="text-xs text-foreground-600 leading-relaxed">{regla.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}